import { useRef, type CSSProperties } from 'react';

interface RangeControlProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onInput: (value: number) => void;
  onCommit: (value: number) => void;
}

export function RangeControl({ id, label, value, min, max, step = 1, suffix = '', onInput, onCommit }: RangeControlProps) {
  const active = useRef(false);
  const lastPreview = useRef<number | null>(null);
  const progress = `${((value - min) / Math.max(1, max - min)) * 100}%`;
  const readValue = (input: HTMLInputElement) => Number.isFinite(input.valueAsNumber) ? input.valueAsNumber : Number(input.value);
  const preview = (next: number) => {
    if (!Number.isFinite(next)) return;
    active.current = true;
    if (lastPreview.current === next) return;
    lastPreview.current = next;
    onInput(next);
  };
  const finish = (next: number) => {
    if (!active.current || !Number.isFinite(next)) return;
    // React may restore the controlled DOM value before an old iOS WebView
    // delivers touchend/blur. The last live value is the authoritative gesture end.
    const finalValue = lastPreview.current ?? next;
    active.current = false;
    lastPreview.current = null;
    onCommit(finalValue);
  };
  return (
    <label className="range-control" htmlFor={id}>
      <span className="range-control__label"><span>{label}</span><output htmlFor={id}>{Math.round(value * 10) / 10}{suffix}</output></span>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ '--range-progress': progress } as CSSProperties}
        onInput={(event) => preview(readValue(event.currentTarget))}
        onChange={(event) => preview(readValue(event.currentTarget))}
        onPointerUp={(event) => finish(readValue(event.currentTarget))}
        onPointerCancel={(event) => finish(readValue(event.currentTarget))}
        onTouchEnd={(event) => finish(readValue(event.currentTarget))}
        onTouchCancel={(event) => finish(readValue(event.currentTarget))}
        onMouseUp={(event) => finish(readValue(event.currentTarget))}
        onKeyUp={(event) => finish(readValue(event.currentTarget))}
        onBlur={(event) => finish(readValue(event.currentTarget))}
      />
    </label>
  );
}
